/**
 * A hazard marked not present on the recce (MB's R243, 5 Nov 2026; docs/specs/map.md §36): a pilot taps a hazard on the
 * phone's map and presses "Not present"; a pilot, operator or manager presses "Present" to undo it. The mark is a
 * record of the project (`hazard_mark`, Postgres 20261105140000_hazard_mark.sql, Teslyn Field SQLite 0080); it never
 * removes a hazard: the hazard stays on every map and in the brief, in green with its own icon, labelled
 * "Not present, marked by <name> <date>".
 *
 * The key. A pack's hazards come from a KML with no id of their own and the pack is built again whenever the hazard maps
 * change, so a mark is kept on what a new version keeps (`hazardMarkKey`): the hazard's kind (point, line or area), its
 * name, and its position rounded to 4 decimal places (about 11 m). A line or area's position is its box, so a line drawn
 * the other way or an area that starts at another corner is the same hazard. Folders, counts and order are left out:
 * they change from one version to the next. A pin and its copy in the pack have the same key, and so do a shape file
 * hazard and its copy; the name is read as the pack writes it (`short_tower_name`, hazard_pack.rs), so a tower of a hazard
 * map has one key on Teslyn Field's map and in the pack.
 *
 * A hazard moved more than the rounding, or renamed, has a new key and shows as present again: a mark that no longer
 * matches never turns another hazard green. That is the safe way round.
 *
 * Each hazard of a project has one row (`hazardMarkId` makes its id from the project and key), so two phones marking the
 * same hazard with no signal meet on one row, and the later press wins (the database's guard).
 *
 * In the KML (decision 1053, `markPackKml`): a marked hazard shows green in what pilots load into Oz Runways, with the
 * words in its text. The stored pack is never changed: the marks are laid over it each time it is served, so a mark needs
 * no new version and no Publish, and "Present" takes it off the same way.
 *
 * Copied to supabase/functions/_shared/ by tools/dev/copy-edge-shared.mjs: it imports nothing.
 */

/** A point of a hazard. */
export interface HazardMarkPoint {
  readonly lat: number;
  readonly lng: number;
}

/** What a hazard's key is made from. */
export type HazardMarkShape =
  | { readonly type: 'point'; readonly at: HazardMarkPoint }
  | { readonly type: 'line'; readonly points: readonly HazardMarkPoint[] }
  | { readonly type: 'polygon'; readonly points: readonly HazardMarkPoint[] };

export type HazardMarkSource = 'pin' | 'pack' | 'shape';
export type HazardMarkState = 'not_present' | 'present';

/** One hazard's mark (`hazard_mark`). Teslyn Cloud adds tenant_id and marked_by (the sign-in). */
export interface HazardMarkRecord {
  readonly id: string;
  readonly project_id: string;
  /** `hazardMarkKey`. */
  readonly hazard_key: string;
  readonly source: HazardMarkSource;
  readonly name: string;
  readonly lat: number;
  readonly lng: number;
  readonly state: HazardMarkState;
  /** When the button was pressed (ISO), on the phone's clock: a press sent later keeps its own time. */
  readonly marked_at: string;
  /** Who pressed it (Teslyn Cloud writes the name from the staff register). */
  readonly marked_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly updated_by: string | null;
  readonly deleted_at: string | null;
}

/** What a map or brief needs of a mark. */
export type HazardMarkView = Pick<
  HazardMarkRecord,
  'hazard_key' | 'state' | 'marked_at' | 'marked_by_name'
> & { readonly deleted_at?: string | null };

/** The hazards marked not present, by key. */
export type HazardMarks = ReadonlyMap<string, HazardMarkView>;

export const NO_HAZARD_MARKS: HazardMarks = new Map();

/** Decimal places of a key's position: 4 is about 11 m on the ground. */
const KEY_PLACES = 4;
/** A very long name still makes a key the database takes. */
const KEY_NAME_MAX = 160;

const place = (n: number): string => n.toFixed(KEY_PLACES);

/** The name as the pack writes it: "Site: 19131 Height: 103.0m" reads "Height: 103.0m" (`short_tower_name`). */
const keyName = (name: string): string =>
  name
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/^site:\s*\d+\s*(?=height:)/, '')
    .slice(0, KEY_NAME_MAX);

function boxOf(points: readonly HazardMarkPoint[]): {
  south: number;
  west: number;
  north: number;
  east: number;
} | null {
  if (points.length === 0) return null;
  let south = Infinity;
  let west = Infinity;
  let north = -Infinity;
  let east = -Infinity;
  for (const p of points) {
    south = Math.min(south, p.lat);
    north = Math.max(north, p.lat);
    west = Math.min(west, p.lng);
    east = Math.max(east, p.lng);
  }
  return { south, west, north, east };
}

/** The key a mark is kept on: `kind|name|position`. Null for a shape with no points. */
export function hazardMarkKey(name: string, shape: HazardMarkShape): string | null {
  if (shape.type === 'point') return `point|${keyName(name)}|${place(shape.at.lat)},${place(shape.at.lng)}`;
  const b = boxOf(shape.points);
  if (!b) return null;
  return `${shape.type}|${keyName(name)}|${place(b.south)},${place(b.west)},${place(b.north)},${place(b.east)}`;
}

/** Where a mark says its hazard is: the point, or the middle of its box. */
export function hazardMarkAt(shape: HazardMarkShape): HazardMarkPoint | null {
  if (shape.type === 'point') return shape.at;
  const b = boxOf(shape.points);
  return b ? { lat: (b.south + b.north) / 2, lng: (b.west + b.east) / 2 } : null;
}

/** One 32-bit lane of the row's id. */
function lane(text: string, seed: number): number {
  let h = seed | 0;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
    h ^= h >>> 15;
  }
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * The row's id, the same on every phone: a UUID made from the project and the key, so two phones that mark a hazard with
 * no signal write one row. Not a secret: RLS decides who may write it.
 */
export function hazardMarkId(projectId: string, key: string): string {
  const text = `${projectId}|${key}`;
  const hex = [0x811c9dc5, 0x9e3779b9, 0x517cc1b7, 0x27d4eb2f]
    .map((seed) => lane(text, seed).toString(16).padStart(8, '0'))
    .join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-8${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

/** The `hazard_mark` row the phone sends through `sync_apply`. */
export function hazardMarkRow(input: {
  readonly projectId: string;
  readonly key: string;
  readonly source: HazardMarkSource;
  readonly name: string;
  readonly at: HazardMarkPoint;
  readonly state: HazardMarkState;
  readonly now: string;
  /** Who is pressing, as this phone knows them; Teslyn Cloud writes it again from the staff register. */
  readonly by: string;
}): HazardMarkRecord {
  return {
    id: hazardMarkId(input.projectId, input.key),
    project_id: input.projectId,
    hazard_key: input.key,
    source: input.source,
    name: input.name.trim().slice(0, 200) || 'Hazard',
    lat: input.at.lat,
    lng: input.at.lng,
    state: input.state,
    marked_at: input.now,
    marked_by_name: input.by.slice(0, 200),
    created_at: input.now,
    updated_at: input.now,
    updated_by: null,
    deleted_at: null,
  };
}

/**
 * The marks in force: per hazard the latest press wins, later lists over earlier ones on equal times (what is waiting on
 * the phone over what was read), and a "Present" takes the hazard off.
 */
export function hazardMarksOf<M extends HazardMarkView>(
  ...lists: ReadonlyArray<readonly M[]>
): ReadonlyMap<string, M> {
  const latest = new Map<string, M>();
  for (const list of lists)
    for (const m of list) {
      const was = latest.get(m.hazard_key);
      if (!was || Date.parse(m.marked_at) >= Date.parse(was.marked_at)) latest.set(m.hazard_key, m);
    }
  const out = new Map<string, M>();
  for (const [key, m] of latest)
    if (m.state === 'not_present' && (m.deleted_at ?? null) === null) out.set(key, m);
  return out;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "5 Nov 2026": in the reader's own time, or `offsetHours` from UTC for a file made away from its reader. */
export function hazardMarkDate(iso: string, offsetHours?: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (offsetHours === undefined) return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  const at = new Date(d.getTime() + offsetHours * 3_600_000);
  return `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}`;
}

/** "Not present, marked by Jo Citizen 5 Nov 2026": every map and brief says this, never green alone. */
export function hazardMarkLabel(
  m: Pick<HazardMarkView, 'marked_at' | 'marked_by_name'>,
  offsetHours?: number,
): string {
  const by = m.marked_by_name.trim();
  return ['Not present, marked', by ? `by ${by}` : '', hazardMarkDate(m.marked_at, offsetHours)]
    .filter(Boolean)
    .join(' ');
}

/**
 * The hazard map's own frame, which is not a hazard to mark: its "Search Area" and its "BND: <file>" boundary folders.
 * The search area is `isSearchArea`'s rule of the brief (R225).
 */
export function isHazardFrame(layerName: string, folder: string): boolean {
  const isArea = (name: string): boolean => name.split(' › ')[0]!.trim().toLowerCase() === 'search area';
  return /^BND:/i.test(layerName.trim()) || isArea(layerName) || isArea(folder);
}

/** A placemark of the pack as `markPlacemarks` reads it. */
interface PlacemarkLike {
  readonly name: string;
  readonly folder: string;
  readonly shape:
    | { readonly type: 'point'; readonly at: HazardMarkPoint }
    | { readonly type: 'line'; readonly points: readonly HazardMarkPoint[] }
    | { readonly type: 'polygon'; readonly outer: readonly HazardMarkPoint[] };
}

/** A placemark's key; null for the map's own frame. */
export function placemarkMarkKey(layerName: string, m: PlacemarkLike): string | null {
  if (isHazardFrame(layerName, m.folder)) return null;
  return hazardMarkKey(
    m.name,
    m.shape.type === 'polygon' ? { type: 'polygon', points: m.shape.outer } : m.shape,
  );
}

/** A placemark's look when marked: the line, dot and any fill in green. Its image, if it has one, stays. */
export function markedPlacemarkStyle<
  S extends { readonly line: string | null; readonly dot: string | null; readonly fill: string | null },
>(s: S, green: string): S {
  return { ...s, line: green, dot: green, fill: s.fill ? green : null };
}

/**
 * The hazards of these layers with their marks: only hazards already in the layers get one, so a mark says nothing of a
 * hazard the reader does not see (the client's copy of the pack).
 */
export function markPlacemarks<
  P extends PlacemarkLike,
  L extends { readonly kind: string; readonly name: string; readonly placemarks: readonly P[] },
>(
  layers: readonly L[],
  marks: HazardMarks,
): Array<L & { readonly placemarks: ReadonlyArray<P & { readonly mark?: { by: string; at: string } }> }> {
  return layers.map((l) => ({
    ...l,
    placemarks: l.placemarks.map((p) => {
      if (l.kind !== 'hazards') return p;
      const key = placemarkMarkKey(l.name, p);
      const m = key ? marks.get(key) : undefined;
      return m ? { ...p, mark: { by: m.marked_by_name, at: m.marked_at } } : p;
    }),
  }));
}

// ------------------------------------------------------------------------------------------------ the KML (decision 1053)

/**
 * The green of a marked hazard in a KML, aabbggrr: `map-line-not-present`'s light value, as an Oz Runways chart is light
 * and a KML line has no dark edge. A test keeps it equal to the token.
 */
export const KML_NOT_PRESENT_COLOUR = 'ff46a31e';
const KML_NOT_PRESENT_BGR = KML_NOT_PRESENT_COLOUR.slice(2);
/** A marked area with no fill of its own, as a pack's survey boundary is filled. */
const KML_NOT_PRESENT_FILL = `40${KML_NOT_PRESENT_BGR}`;

/** The pack's folders that are not hazards: the phones never mark them (`portalHazards`'s own list). */
const NOT_HAZARDS = /^(Survey Lines|Survey Boundaries|Pinned Properties) \(\d+\)$/;

const xmlText = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const decodeXml = (s: string): string =>
  s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, n: string) => String.fromCodePoint(Number.parseInt(n, 16)))
    .replace(/&amp;/g, '&');

/** An element's text as `portalHazards` reads it: escapes decoded, CDATA as it is, comments left out. */
const kmlText = (raw: string): string =>
  raw
    .replace(/<!--[\s\S]*?-->/g, '')
    .split(/(<!\[CDATA\[[\s\S]*?\]\]>)/)
    .map((part) => (part.startsWith('<![CDATA[') ? part.slice(9, -3) : decodeXml(part)))
    .join('')
    .trim();

/** `lng,lat[,alt]` pairs, as `portalHazards` reads them. */
function kmlPoints(text: string): HazardMarkPoint[] {
  const out: HazardMarkPoint[] = [];
  for (const t of text.trim().split(/\s+/)) {
    if (!t) continue;
    const [lng, lat] = t.split(',').map((v) => Number.parseFloat(v));
    if (lat === undefined || lng === undefined || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;
    out.push({ lat, lng });
  }
  return out;
}

/** A placemark's shape from its text (its description out of the way); null for what no hazard is. */
function placemarkShape(block: string): HazardMarkShape | null {
  const coords = (tag: string): HazardMarkPoint[] | null => {
    const at = block.indexOf(`<${tag}>`);
    if (at < 0) return null;
    const m = /<coordinates>([\s\S]*?)<\/coordinates>/.exec(block.slice(at));
    return m ? kmlPoints(m[1]!) : null;
  };
  const point = coords('Point');
  if (point) return point[0] ? { type: 'point', at: point[0] } : null;
  const line = coords('LineString');
  if (line) return line.length >= 2 ? { type: 'line', points: line } : null;
  const outer = coords('outerBoundaryIs') ?? coords('Polygon');
  return outer && outer.length >= 3 ? { type: 'polygon', points: outer } : null;
}

/**
 * A copy of a shared style in green, kept to Style and StyleUrl, the only styling Oz Runways documents (docs/specs/map.md
 * §33), so no inline style and no new icon: `<color>` tints the same Google icon as the pack's towers are tinted, and each
 * line and area keeps its width. The label stays on, whatever the style said, so the words show on the map even if an app
 * ignores an icon's tint. Null for a pack pin: its picture is a PNG inside the file, which a tint would only muddy.
 */
function greenStyle(body: string, kind: HazardMarkShape['type']): string | null {
  if (/<href>\s*data:/.test(body)) return null;
  const tinted = (text: string, tag: string, colour: (old: string) => string): string =>
    text.replace(new RegExp(`<${tag}>[\\s\\S]*?</${tag}>`), (part) =>
      /<color>/.test(part)
        ? part.replace(
            /<color>\s*([0-9a-fA-F]{8})?\s*<\/color>/,
            (_, old: string | undefined) => `<color>${colour(old ?? 'ff')}</color>`,
          )
        : part.replace(`<${tag}>`, `<${tag}><color>${colour('ff')}</color>`),
    );
  let out = body.replace(/<LabelStyle>[\s\S]*?<\/LabelStyle>/, '');
  out = tinted(out, 'IconStyle', () => KML_NOT_PRESENT_COLOUR);
  out = tinted(out, 'LineStyle', () => KML_NOT_PRESENT_COLOUR);
  // an area keeps how see-through it was
  out = tinted(out, 'PolyStyle', (old) => `${old.slice(0, 2)}${KML_NOT_PRESENT_BGR}`);
  if (kind === 'point' && !out.includes('<IconStyle>'))
    out = `<IconStyle><color>${KML_NOT_PRESENT_COLOUR}</color></IconStyle>${out}`;
  if (kind !== 'point' && !out.includes('<LineStyle>'))
    out += `<LineStyle><color>${KML_NOT_PRESENT_COLOUR}</color><width>2</width></LineStyle>`;
  if (kind === 'polygon' && !out.includes('<PolyStyle>'))
    out += `<PolyStyle><color>${KML_NOT_PRESENT_FILL}</color></PolyStyle>`;
  return out;
}

/** A placemark with the words in its name and first in its description. */
function placemarkWithWords(block: string, words: string): string {
  const named = /<name>([\s\S]*?)<\/name>/.test(block)
    ? block.replace(
        /<name>([\s\S]*?)<\/name>/,
        (_, raw: string) => `<name>${xmlText(`Not present: ${kmlText(raw)}`)}</name>`,
      )
    : block.replace('<Placemark>', `<Placemark><name>Not present</name>`);
  const said = xmlText(words);
  if (/<description>\s*<!\[CDATA\[/.test(named))
    return named.replace(/<description>\s*<!\[CDATA\[/, (open) => `${open}${said}\n\n`);
  if (named.includes('<description>'))
    return named.replace('<description>', () => `<description>${said}\n\n`);
  return named.replace('</name>', () => `</name><description><![CDATA[${said}]]></description>`);
}

/**
 * The pack as a pilot gets it (decision 1053): each marked hazard green, its name starting "Not present:" and its
 * description with "Not present, marked by <name> <date>". Nothing is dropped, and a hazard is never removed. Only hazards
 * are looked at: the map's frame, the survey lines and boundaries and the landholder properties never match, and a mark
 * with no hazard here changes nothing. With no match the answer is `kml` itself, so a file with no marks is the stored
 * file to the byte.
 *
 * A pack pin's picture cannot be recoloured: it keeps its picture, and the words in its name and description say it.
 *
 * The date is the hazard's own day, the file being made on a server in UTC: its longitude gives the hour offset, near
 * enough for one day in Australia. One pass over the text, so it costs little and nothing with no marks.
 */
export function markPackKml(kml: string, marks: HazardMarks): string {
  if (marks.size === 0) return kml;
  const tokens = /<!\[CDATA\[[\s\S]*?\]\]>|<Folder>|<\/Folder>|<Placemark>[\s\S]*?<\/Placemark>/g;
  const folders: string[] = [];
  const styles = new Map<string, { readonly id: string; readonly body: string }>();
  let out = '';
  let at = 0;
  let head = -1;
  let shared: Map<string, string> | null = null;
  const styleBody = (id: string): string | undefined => {
    if (!shared) {
      shared = new Map();
      const end = head < 0 ? kml.length : head;
      for (const m of kml.slice(0, end).matchAll(/<Style id="([^"]+)">([\s\S]*?)<\/Style>/g))
        shared.set(m[1]!, m[2]!);
    }
    return shared.get(id);
  };
  for (let m = tokens.exec(kml); m; m = tokens.exec(kml)) {
    const token = m[0];
    if (token === '<Folder>') {
      if (head < 0) head = m.index;
      const name = /^\s*<name>([\s\S]*?)<\/name>/.exec(kml.slice(m.index + 8, m.index + 8 + 1000));
      folders.push(name ? kmlText(name[1]!) : '');
      continue;
    }
    if (token === '</Folder>') {
      folders.pop();
      continue;
    }
    if (!token.startsWith('<Placemark>')) continue;
    if (head < 0) head = m.index;
    const layer = folders[0] ?? '';
    if (NOT_HAZARDS.test(layer.trim()) || isHazardFrame(layer, folders.slice(1).join(' › '))) continue;
    const plain = token.replace(/<description>[\s\S]*?<\/description>/, '');
    const shape = placemarkShape(plain);
    if (!shape) continue;
    const name = /<name>([\s\S]*?)<\/name>/.exec(plain);
    const key = hazardMarkKey(name ? kmlText(name[1]!) : '', shape);
    const mark = key ? marks.get(key) : undefined;
    if (!mark) continue;
    const lng = hazardMarkAt(shape)?.lng ?? 0;
    let block = placemarkWithWords(token, hazardMarkLabel(mark, Math.round(lng / 15)));
    const url = /<styleUrl>\s*#([^<\s]+)\s*<\/styleUrl>/.exec(plain);
    const was = url ? url[1]! : '';
    const cache = `${shape.type}|${was}`;
    let style = styles.get(cache);
    if (!style) {
      const body = greenStyle(styleBody(was) ?? '', shape.type);
      if (body !== null) {
        style = { id: `teslynNotPresent${styles.size + 1}`, body };
        styles.set(cache, style);
      }
    }
    if (style) {
      const to = `<styleUrl>#${style.id}</styleUrl>`;
      block = url
        ? block.replace(/<styleUrl>[\s\S]*?<\/styleUrl>/, () => to)
        : block.replace('</name>', () => `</name>${to}`);
    }
    out += kml.slice(at, m.index) + block;
    at = m.index + token.length;
  }
  if (at === 0) return kml;
  out += kml.slice(at);
  // the shared styles, where the pack keeps its own: before the first folder or placemark
  const added = [...styles.values()].map((s) => `<Style id="${s.id}">${s.body}</Style>\n`).join('');
  return added ? `${out.slice(0, head)}${added}${out.slice(head)}` : out;
}
